// Editar camada.
//
// A rota `PATCH /api/camadas/:id` existia e a interface só a usava para o
// `ativo` — nome, endpoint e modo de publicação não tinham como ser corrigidos.
// Quem abria a camada esperando editar encontrava a gaveta de curadoria.
//
// **Tema e fonte ficam de fora**, e é decisão, não esquecimento: trocar a fonte
// muda a procedência de tudo que já foi ingerido — a licença que saiu no
// metadado de uma versão publicada foi a da fonte ANTIGA. Quem precisa disso
// cria outra camada.

import { useState, type ReactNode } from "react";
import type { Camada } from "../api.js";
import { Gaveta } from "../pecas/basicos.js";
import { DescobertaWfs } from "../pecas/DescobertaWfs.js";

export function EditarCamada({
  camada,
  aoFechar,
  aoSalvar,
  salvando,
}: {
  camada: Camada;
  aoFechar: () => void;
  aoSalvar: (corpo: Record<string, unknown>) => void;
  salvando: boolean;
}): ReactNode {
  const [nome, setNome] = useState(camada.nome);
  const [endpoint, setEndpoint] = useState(camada.endpoint_url ?? "");
  const [parametros, setParametros] = useState<Record<string, unknown>>(camada.parametros ?? {});
  const wfs = camada.tipo_conector === "ogc";
  const [modo, setModo] = useState(camada.modo_publicacao);
  const [ingestao, setIngestao] = useState(camada.modo_ingestao);
  const [vazias, setVazias] = useState(
    camada.execucoes_vazias_para_alertar === null
      ? ""
      : String(camada.execucoes_vazias_para_alertar),
  );

  return (
    <Gaveta titulo={`Editar ${camada.nome}`} subtitulo={camada.tipo_conector} aoFechar={aoFechar}>
      <div className="form-secao">
        <div className="form-secao-titulo">Identificação</div>
        <div className="campo">
          <label htmlFor="ed-nome">Nome</label>
          <input id="ed-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="ed-endpoint">Endpoint</label>
          <input id="ed-endpoint" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} />
          <div className="campo-ajuda">Vazio para camada que só recebe arquivo.</div>
        </div>
        {wfs ? (
          <DescobertaWfs
            endpoint={endpoint}
            fonteId={camada.fonte_id ?? null}
            parametros={parametros}
            aoMudar={setParametros}
            prefixo="ed-wfs"
          />
        ) : null}
      </div>

      <div className="form-secao">
        <div className="form-secao-titulo">Publicação</div>
        <div className="campo">
          <label htmlFor="ed-modo">Modo</label>
          <select id="ed-modo" value={modo} onChange={(e) => setModo(e.target.value)}>
            <option value="manual">manual</option>
            <option value="automatico">automático</option>
          </select>
          <div className="campo-ajuda">
            Em automático o agendador valida e promove — e <b>não publica</b>. Publicar muda o que o
            cliente vê, e o gate de licença mora ali.
          </div>
        </div>
        <div className="campo-ajuda">
          Tema e fonte não se alteram: trocar a fonte mudaria a procedência do que já foi ingerido,
          e a licença que saiu no metadado de uma versão publicada foi a da fonte antiga. O{" "}
          <b>dono</b> também não: a camada teria versões seladas de um cliente e passaria a ser de
          outro.
        </div>
      </div>

      <div className="form-secao">
        <div className="form-secao-titulo">Ingestão</div>
        <div className="campo">
          <label htmlFor="ed-ingestao">O que a resposta da fonte é</label>
          <select
            id="ed-ingestao"
            value={ingestao}
            onChange={(e) => setIngestao(e.target.value as Camada["modo_ingestao"])}
          >
            <option value="espelho">Espelho — a resposta É a base inteira</option>
            <option value="acumulativo">Acumulativo — a resposta acrescenta</option>
          </select>
          <div className="campo-ajuda">
            Em <b>espelho</b> (SIGEF, CAR, IBGE) a resposta substitui a base, então encolher demais{" "}
            <b>reprova</b>: é o modo de falha mais caro de fonte pública — HTTP 200 com conjunto
            truncado. Em <b>acumulativo</b> (detecção) cada achado soma ao que já existe, e uma
            passada vazia não apaga nada: encolher vira <b>aviso</b>.
          </div>
        </div>
        {ingestao === "acumulativo" ? (
          <div className="campo">
            <label htmlFor="ed-vazias">Execuções vazias seguidas que viram alerta</label>
            <input
              id="ed-vazias"
              type="number"
              min="1"
              value={vazias}
              onChange={(e) => setVazias(e.target.value)}
            />
            <div className="campo-ajuda">
              É o que substitui a trava: zero achados num dia é resultado, mas zero por N execuções
              seguidas é a fonte quebrada — e tem a mesma cara. Vazio significa <b>não alertar</b>,
              e é escolha legítima para fonte que passa meses sem detecção: alerta que dispara
              sempre é alerta que se aprende a ignorar.
            </div>
          </div>
        ) : null}
      </div>

      <div className="acoes-form">
        <button
          className="btn btn-primario"
          disabled={nome.trim() === "" || salvando}
          onClick={() =>
            aoSalvar({
              nome: nome.trim(),
              endpoint_url: endpoint.trim() || null,
              // O PATCH substitui o jsonb inteiro; o estado nasceu do valor
              // atual, então o que não foi tocado volta igual.
              ...(wfs ? { parametros } : {}),
              modo_publicacao: modo,
              modo_ingestao: ingestao,
              // Em espelho o campo some da tela, e mandar o valor antigo o
              // deixaria pendurado: uma camada que volta a ser espelho não tem
              // alerta de silêncio para configurar.
              execucoes_vazias_para_alertar:
                ingestao === "acumulativo" && vazias.trim() !== "" ? Number(vazias) : null,
            })
          }
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button className="btn" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </Gaveta>
  );
}
